import { DexConfig, DexProtocol } from "@cede/types";
import { DEX_CONFIG_LIST, DEX_PROTOCOLS } from "@cede/utils";
import Decimal from "decimal.js";
import { defineChain } from "viem";

export const NATIVE_TOKEN_ADDRESS = "0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE";

export const DEFAULT_SLIPPAGE = new Decimal(1); // 1%

/**
 * Robinhood Chain (Arbitrum Orbit L2, ETH gas). Mirrors viem's `robinhood` definition,
 * which only ships from viem 2.56 — hub-v2 pins 2.42. The RPC is CORS-open.
 */
export const robinhoodChain = defineChain({
  id: 4663,
  name: "Robinhood Chain",
  nativeCurrency: { name: "Ether", symbol: "ETH", decimals: 18 },
  // viem derives receipt polling from it: 500 ms here, 4000 ms when unset.
  blockTime: 100,
  rpcUrls: { default: { http: ["https://rpc.mainnet.chain.robinhood.com"] } },
  blockExplorers: {
    default: {
      name: "Blockscout",
      url: "https://robinhoodchain.blockscout.com",
      apiUrl: "https://robinhoodchain.blockscout.com/api",
    },
  },
  contracts: {
    multicall3: { address: "0xca11bde05977b3631167028862be2a173976ca11" },
  },
});

export const SUPPORTED_CHAINS = [1, 137, 10, 56, 42161, 8453, 4663] as const; // Mainnet, Polygon, Optimism, BNB Chain, Arbitrum, Base, Robinhood Chain

export const ERC20_ABI = [
  {
    name: "approve",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [
      { name: "spender", type: "address" },
      { name: "amount", type: "uint256" },
    ],
    outputs: [{ type: "bool" }],
  },
  {
    name: "allowance",
    type: "function",
    stateMutability: "view",
    inputs: [
      { name: "owner", type: "address" },
      { name: "spender", type: "address" },
    ],
    outputs: [{ type: "uint256" }],
  },
  {
    name: "transfer",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [
      { name: "to", type: "address" },
      { name: "amount", type: "uint256" },
    ],
    outputs: [{ type: "bool" }],
  },
  {
    name: "balanceOf",
    type: "function",
    stateMutability: "view",
    inputs: [{ name: "account", type: "address" }],
    outputs: [{ type: "uint256" }],
  },
] as const;

/**
 * DEX Configurations derived from centralized DEX_CONFIG in @cede/utils.
 * Maps DexProtocol to DexConfig for UI display.
 */
export const DEX_CONFIGS: Record<DexProtocol, DexConfig> = Object.fromEntries(
  DEX_CONFIG_LIST.map((config) => [
    config.protocol,
    {
      id: config.protocol,
      name: config.name,
      logo: `/dexs/${config.logoFile}.svg`,
      supportedChains: [config.chainId],
      mobulaProtocolId: config.protocol,
      description: config.description,
      exchangeId: config.exchangeId,
      chainName: config.chainName,
      campaignOnly: config.campaignOnly,
    } satisfies DexConfig,
  ]),
) as Record<DexProtocol, DexConfig>;

export const AVAILABLE_DEXS = Object.values(DEX_CONFIGS);

/** DEXes offered outside a campaign page, where no `poolAddress` pins the route. */
export const GENERAL_SWAP_DEXS = AVAILABLE_DEXS.filter((dex) => !dex.campaignOnly);

export const DEFAULT_DEX: DexProtocol = DEX_PROTOCOLS.AERODROME_CL;
